'use client'

import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { FindingList } from '@/components/evaluation/finding-list'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export type AnalysisFindingsCardsProps = {
  highlights: string[]
  redFlags: string[]
  highlightsTitle?: string
  redFlagsTitle?: string
  maxHighlights?: number
  maxRedFlags?: number
  /** Two columns from `sm` when both lists are non-empty (full analysis page). */
  twoColumn?: boolean
  className?: string
}

/** Shared tinted Highlights / Gaps cards for analysis page and canvas verdict. */
export function AnalysisFindingsCards({
  highlights,
  redFlags,
  highlightsTitle = 'Highlights',
  redFlagsTitle = 'Gaps',
  maxHighlights,
  maxRedFlags,
  twoColumn = false,
  className,
}: AnalysisFindingsCardsProps) {
  if (highlights.length === 0 && redFlags.length === 0) return null

  return (
    <div
      className={cn(
        'grid gap-3',
        twoColumn && highlights.length > 0 && redFlags.length > 0 && 'sm:grid-cols-2',
        className,
      )}
    >
      {highlights.length > 0 ? (
        <Card className="border-success/30 bg-success/10">
          <CardHeader className="p-0 pb-2">
            <CardTitle className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-success">
              <ThumbsUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {highlightsTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FindingList items={highlights} tone="success" max={maxHighlights} />
          </CardContent>
        </Card>
      ) : null}
      {redFlags.length > 0 ? (
        <Card className="border-warning/30 bg-warning/10">
          <CardHeader className="p-0 pb-2">
            <CardTitle className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-warning">
              <ThumbsDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {redFlagsTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FindingList items={redFlags} tone="warning" max={maxRedFlags} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
